/**
 * Date utility functions for ISO 8601 UTC date formatting.
 * All date values sent to the backend must be in ISO 8601 UTC format
 * (e.g. "2026-04-02T10:12:42.000Z") for PostgreSQL TIMESTAMPTZ compatibility.
 */

/**
 * Convert any date value to ISO 8601 UTC string.
 * Handles multiple input formats:
 *   - DD-MM-YYYY
 *   - DD-MM-YYYY HH:mm:ss
 *   - YYYY-MM-DD
 *   - YYYY-MM-DDTHH:mm:ss
 *   - ISO 8601 string
 *   - Date object
 *   - Unix timestamp
 *
 * @param value - The date value to convert
 * @returns ISO 8601 UTC string (e.g. "2026-04-02T10:12:42.000Z") or null for empty/invalid values
 */
export const toUTC = (value: any): string | null => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Handle DD-MM-YYYY and DD-MM-YYYY HH:mm:ss formats
    const ddmmyyyy = trimmed.match(
      /^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/
    );
    if (ddmmyyyy) {
      const [, day, month, year, h, m, s] = ddmmyyyy;
      const date = new Date(
        +year,
        +month - 1,
        +day,
        +(h || 0),
        +(m || 0),
        +(s || 0)
      );
      return isNaN(date.getTime()) ? null : date.toISOString();
    }

    // Handle DD/MM/YYYY format
    const ddmmyyyySlash = trimmed.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/
    );
    if (ddmmyyyySlash) {
      const [, day, month, year, h, m, s] = ddmmyyyySlash;
      const date = new Date(
        +year,
        +month - 1,
        +day,
        +(h || 0),
        +(m || 0),
        +(s || 0)
      );
      return isNaN(date.getTime()) ? null : date.toISOString();
    }

    // Handle YYYY-MM-DD (date-only, no T separator)
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      // Treat date-only strings as UTC midnight to avoid timezone day-shift.
      const date = new Date(trimmed + "T00:00:00Z");
      return isNaN(date.getTime()) ? null : date.toISOString();
    }
  }

  // Fallback: let Date constructor handle it (ISO strings, timestamps, Date objects, etc.)
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * Convert any date value to a local display string (DD-MM-YYYY HH:mm:ss or DD-MM-YYYY).
 * Used for formatting ISO strings back to a readable local time for the UI.
 *
 * @param value - The date value to format
 * @param includeTime - Whether to include the time portion
 * @returns Formatted local date string or the original value if invalid
 */
export const toLocalDisplay = (value: any, includeTime: boolean = false): string => {
  if (value === null || value === undefined || value === "") return "";

  let date: Date;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";

    // 1. Handle legacy DD-MM-YYYY [HH:mm:ss] format from backend
    const ddmmyyyy = trimmed.match(
      /^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/
    );
    if (ddmmyyyy) {
      const [, day, month, year, h, m, s] = ddmmyyyy;
      // Treat as UTC to ensure it converts to local time correctly
      date = new Date(Date.UTC(+year, +month - 1, +day, +(h || 0), +(m || 0), +(s || 0)));
    } 
    // 2. Handle ISO strings with 'T'
    else if (trimmed.includes("T")) {
      let parseValue = trimmed;
      // If no timezone/offset is provided, assume UTC ('Z') for consistency with our backend refactor
      if (!trimmed.includes("Z") && !trimmed.match(/[+-]\d{2}:?\d{2}$/)) {
        parseValue += "Z";
      }
      date = new Date(parseValue);
    }
    // 3. Fallback
    else {
      date = new Date(trimmed);
    }
  } else {
    date = new Date(value);
  }

  // If invalid, return original stringified value
  if (isNaN(date.getTime())) return String(value);

  const pad = (n: number) => n.toString().padStart(2, "0");
  const datePart = `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;

  if (includeTime) {
    const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    return `${datePart} ${timePart}`;
  }

  return datePart;
};
